import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { absUrl } from '../lib/api';

export default function HeroCarousel({ slides = [], intervalMs = 4500, device = 'mobile' }) {
    const [idx, setIdx] = useState(0);
    const containerRef = useRef(null);
    const startXRef = useRef(null);
    const count = slides.length;

    useEffect(() => {
        if (count <= 1) return;
        const t = setInterval(() => setIdx(i => (i + 1) % count), intervalMs);
        return () => clearInterval(t);
    }, [count, intervalMs]);

    const go = (i) => setIdx(((i % count) + count) % count);

    const onTouchStart = (e) => { startXRef.current = e.touches[0].clientX; };
    const onTouchEnd = (e) => {
        if (startXRef.current == null) return;
        const dx = e.changedTouches[0].clientX - startXRef.current;
        if (Math.abs(dx) > 40) go(dx > 0 ? idx - 1 : idx + 1);
        startXRef.current = null;
    };

    if (!count) return null;

    return (
        <div className="relative select-none" ref={containerRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div className={`relative overflow-hidden bg-[#F5EFE1] ${device === 'mobile' ? 'aspect-square' : 'aspect-[16/8]'}`}>
                {slides.map((s, i) => (
                    <img
                        key={s.id || i}
                        src={absUrl(s.image_url)}
                        alt={s.caption || ''}
                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                        style={{ opacity: i === idx ? 1 : 0 }}
                        draggable={false}
                    />
                ))}
                {slides[idx]?.caption && (
                    <div className="absolute left-0 right-0 bottom-6 text-center px-6">
                        <span className="inline-block bg-black/40 text-white text-xs sm:text-sm font-medium px-3 py-1.5 rounded-full backdrop-blur">{slides[idx].caption}</span>
                    </div>
                )}
            </div>
            {count > 1 && (
                <>
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
                        {slides.map((_, i) => (
                            <button key={i} onClick={() => go(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-sdf-red' : 'w-2 bg-white/70'}`} aria-label={`Slide ${i + 1}`} />
                        ))}
                    </div>
                    <button aria-label="Prev" onClick={() => go(idx - 1)} className="hidden md:flex absolute top-1/2 left-2 -translate-y-1/2 bg-black/40 text-white p-2 rounded-full"><ChevronLeft size={20} /></button>
                    <button aria-label="Next" onClick={() => go(idx + 1)} className="hidden md:flex absolute top-1/2 right-2 -translate-y-1/2 bg-black/40 text-white p-2 rounded-full"><ChevronRight size={20} /></button>
                </>
            )}
        </div>
    );
}
